import { LayoutDashboard, RefreshCw, TriangleAlert } from 'lucide-react';
import { BrandStatusPage } from './BrandStatusPage';

export interface ServerErrorPageProps {
  layout?: 'viewport' | 'embedded';
  onRetry?: () => void;
  /** Safe diagnostic line (path / time only — never PHI). */
  detailLines?: string[];
}

/**
 * Themed 500 / crash page — design from `templates/500.html`.
 */
export function ServerErrorPage({
  layout = 'viewport',
  onRetry,
  detailLines,
}: ServerErrorPageProps): JSX.Element {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const time = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  const lines = detailLines ?? [`Status: 500 Internal Server Error`, `Path: ${path}`, `Time: ${time} IST`];

  return (
    <BrandStatusPage
      layout={layout}
      tone="coral"
      heroTone="coral"
      heroSize="lg"
      heroMotion="shake"
      showLogo={layout === 'viewport'}
      badge={
        <span className="inline-flex items-center gap-2 rounded-lg bg-coral-100 px-3 py-1.5 text-xs font-semibold text-coral-600">
          <span className="h-2 w-2 animate-pulse rounded-full bg-coral-500" />
          System Incident
        </span>
      }
      hero={
        <div className="relative flex h-full w-full flex-col items-center justify-center">
          <span className="font-display text-[58px] leading-none font-semibold tracking-tight text-coral-600 select-none">
            500
          </span>
          <svg
            viewBox="0 0 100 100"
            className="pointer-events-none absolute inset-0 h-full w-full opacity-10"
            aria-hidden
          >
            <polyline
              points="40,10 45,30 35,35 50,60"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              className="text-coral-500"
            />
            <polyline
              points="65,20 60,40 70,45 58,80"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              className="text-brand-400"
            />
          </svg>
          <div className="absolute top-4 right-4 flex h-6 w-6 items-center justify-center rounded bg-coral-500">
            <TriangleAlert className="h-3.5 w-3.5 text-white" aria-hidden />
          </div>
        </div>
      }
      title="Something broke on our end."
      description="Our servers encountered an unexpected issue. Our engineering team has been alerted and is already working on a fix. Please try again in a few minutes."
      detail={
        <div className="bs-error-card mx-auto mb-6 max-w-md text-left">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-semibold text-coral-600">Error Details</span>
          </div>
          <div className="font-mono text-[11px] space-y-1 text-ink-500">
            {lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>
      }
      primaryAction={
        onRetry
          ? {
              label: 'Try Again',
              onClick: onRetry,
              icon: RefreshCw,
              variant: 'coral',
            }
          : {
              label: 'Try Again',
              onClick: () => window.location.reload(),
              icon: RefreshCw,
              variant: 'coral',
            }
      }
      secondaryAction={{
        label: 'Go to Dashboard',
        to: '/',
        icon: LayoutDashboard,
        variant: 'secondary',
      }}
      footer="Error 500 · DentXE"
    />
  );
}
